// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { Webhook, Plus, Send, Pause, Play, Trash2, RotateCcw, Loader2, ListOrdered, History, ChevronDown, ChevronRight, RefreshCw, Download } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import { Pager, useServerPaged, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'
import { MultiSelectDropdown } from './DataApiSettingsTab'

/**
 * D-HOOK-1 — Webhooks for one Data API partner: as soon as a PioneerRx envelope arrives, a copy is
 * POSTed to every hook whose provider filter (NPIs and/or provider groups) matches the writing
 * prescriber. The ONLY control is that filter (Patrick 2026-09-08): a hook never receives another
 * provider's prescriptions or patients. Queue = what is being sent; History = what was sent.
 */
export default function DataApiWebhooksSection({ client, providers, onError }: {
  client: api.DataApiClientRow; providers: api.DataApiProvider[]; onError: (e: string) => void
}) {
  const [list, setList] = useState<api.WebhookList | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const load = () => api.listWebhooks().then(setList).catch(e => onError(friendlyError(e)))
  useEffect(() => { void load() }, [client.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const hooks = (list?.hooks ?? []).filter(h => h.client_id === client.id)
  const atCap = !!list && list.limits.hooks_used >= list.limits.max_webhooks
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-soft)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Webhook size={13} /> Webhooks ({hooks.length})
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8, lineHeight: 1.5 }}>
        Push a copy of each PioneerRx envelope to this partner's endpoint the moment it arrives — <strong>only</strong> envelopes written by the providers in the hook's filter.
        HTTPS, HTTP Basic credentials, a timeout and a rate per minute per hook. {list && <span>{list.limits.hooks_used} of {list.limits.max_webhooks} hooks used for this pharmacy.</span>}
      </div>
      {hooks.map(h => (
        <div key={h.id} style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 8, marginBottom: 8 }}>
          <button type="button" onClick={() => setOpen(o => o === h.id ? null : h.id)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            {open === h.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span style={{ fontSize: 13, fontWeight: 600 }}>{h.name}</span>
            <HookStatus status={h.status} />
            <span style={{ fontSize: 12, color: 'var(--color-text-soft)', marginLeft: 'auto' }}>
              {h.npis.length + h.group_ids.length === 0 ? <span style={{ color: '#dc2626', fontWeight: 600 }}>no filter — sends nothing</span> : `${h.npis.length} NPI · ${h.group_ids.length} group${h.group_ids.length === 1 ? '' : 's'}`}
              {' · '}queue {h.queued + h.sending} · sent 24h {h.sent_24h} · failed 24h {h.failed_24h}
            </span>
          </button>
          {open === h.id && <HookEditor hook={h} providers={providers} groups={list?.provider_groups ?? []} onChanged={load} onError={onError} />}
        </div>
      ))}
      {showNew
        ? <NewHookForm clientId={client.id} providers={providers} groups={list?.provider_groups ?? []}
            onCreated={() => { setShowNew(false); void load() }} onCancel={() => setShowNew(false)} onError={onError} />
        : <button type="button" style={btn} disabled={atCap} onClick={() => setShowNew(true)}><Plus size={13} /> New webhook{atCap ? ' — limit reached' : ''}</button>}
    </div>
  )
}

function HookStatus({ status }: { status: string }) {
  const map: Record<string, [string, string]> = { active: ['#16a34a', 'Active'], pending: ['#d97706', 'Pending'], paused: ['#dc2626', 'Paused'], disabled: ['#6b7280', 'Disabled'] }
  const [color, label] = map[status] ?? ['#6b7280', status]
  return <span style={{ fontSize: 11, fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 10, padding: '1px 8px' }}>{label}</span>
}

/**
 * Maps a server error code to the FIELD it is about. The API returns precise codes
 * (url_must_be_https, timeout_out_of_range, ...); sending all of them to a banner far from the form
 * told the operator something was wrong and not which box to fix (owner feedback, 2026-09-18).
 */
const HOOK_FIELD_ERRORS: Record<string, { field: keyof NewHookFields; message: string }> = {
  name_required: { field: 'name', message: 'A name is required.' },
  url_invalid: { field: 'url', message: 'That is not a valid URL.' },
  url_must_be_https: { field: 'url', message: 'The endpoint must be https:// — envelopes only ever leave over TLS.' },
  url_must_not_embed_credentials: { field: 'url', message: 'Remove the user:password from the URL; use the username and password fields.' },
  url_private_host: { field: 'url', message: 'That host is on a private network and cannot be reached from the platform.' },
  timeout_out_of_range: { field: 'timeoutSec', message: 'Timeout must be between 3 and 120 seconds.' },
  rate_out_of_range: { field: 'rateLimitPerMin', message: 'Rate must be between 1 and 6000 per minute.' },
}

type NewHookFields = { name: string; url: string; username: string; password: string; signingSecret: string; timeoutSec: string; rateLimitPerMin: string }

function NewHookForm({ clientId, providers, groups, onCreated, onCancel, onError }: {
  clientId: string
  providers: api.DataApiProvider[]
  groups: { id: string; name: string }[]
  onCreated: () => void
  onCancel: () => void
  onError: (e: string) => void
}) {
  const [f, setF] = useState<NewHookFields>({ name: '', url: '', username: '', password: '', signingSecret: '', timeoutSec: '15', rateLimitPerMin: '60' })
  const [busy, setBusy] = useState(false)
  const [fieldErr, setFieldErr] = useState<Partial<Record<keyof NewHookFields, string>>>({})

  // THE FILTER, AT CREATE TIME (owner feedback, 2026-09-18). It used to exist only in the editor, so
  // every new hook was born with no filter and the list rendered "no filter — sends nothing" in red.
  // Creating a webhook that sends nothing, then reopening it to make it work, is two steps for one
  // decision — and the filter is the ONLY control on what a partner receives, so it belongs in front
  // of whoever is creating the hook rather than one screen later.
  const [npiSel, setNpiSel] = useState<Set<string>>(new Set())
  const [grpSel, setGrpSel] = useState<Set<string>>(new Set())
  const [extraNpi, setExtraNpi] = useState('')

  const npiItems = Array.from(new Map(providers.filter(p => p.npi).map(p => [p.npi as string, { id: p.npi as string, label: `${p.name} · ${p.npi}` }])).values())
  const groupItems = groups.map(g => ({ id: g.id, label: g.name }))

  const set = (k: keyof NewHookFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setF({ ...f, [k]: e.target.value })
    if (fieldErr[k]) setFieldErr({ ...fieldErr, [k]: undefined })   // clear as they fix it
  }

  const addExtraNpi = () => {
    const n = extraNpi.trim()
    if (/^\d{10}$/.test(n)) { setNpiSel(new Set([...npiSel, n])); setExtraNpi('') }
    else onError('An NPI is 10 digits')
  }

  async function submit() {
    // Validate locally first, against the same rules the server enforces, so the common mistakes
    // never cost a round trip.
    const errs: Partial<Record<keyof NewHookFields, string>> = {}
    if (!f.name.trim()) errs.name = 'A name is required.'
    if (!f.url.trim()) errs.url = 'An endpoint URL is required.'
    else if (!/^https:\/\//i.test(f.url.trim())) errs.url = 'The endpoint must be https:// — envelopes only ever leave over TLS.'
    const t = Number(f.timeoutSec), r = Number(f.rateLimitPerMin)
    if (!Number.isFinite(t) || t < 3 || t > 120) errs.timeoutSec = 'Between 3 and 120 seconds.'
    if (!Number.isFinite(r) || r < 1 || r > 6000) errs.rateLimitPerMin = 'Between 1 and 6000 per minute.'
    if (Object.keys(errs).length) { setFieldErr(errs); return }

    setBusy(true)
    setFieldErr({})
    try {
      const created = await api.createWebhook(clientId, {
        name: f.name.trim(), url: f.url.trim(), username: f.username.trim() || undefined, password: f.password || undefined,
        signingSecret: f.signingSecret || undefined, timeoutSec: t, rateLimitPerMin: r,
      })
      // Filters are a second call by design (the API keeps creation and scope separate), but the
      // operator chose them in one form and should not have to remember the second step.
      if (created?.id && (npiSel.size || grpSel.size)) {
        try { await api.setWebhookFilters(created.id, [...npiSel], [...grpSel]) }
        catch (e) { onError(`Webhook created, but the provider filter did not save: ${friendlyError(e)}. Open the hook and set it.`) }
      }
      onCreated()
    } catch (e: any) {
      // Put the message ON the field the server named. Anything unrecognised still goes to the
      // banner — better a generic message than a silent failure.
      const code = e?.body?.error as string | undefined
      const mapped = code ? HOOK_FIELD_ERRORS[code] : undefined
      if (mapped) setFieldErr({ [mapped.field]: mapped.message })
      else onError(friendlyError(e))
    } finally { setBusy(false) }
  }

  const Field = ({ k, label, children }: { k: keyof NewHookFields; label: string; children: React.ReactNode }) => (
    <label style={lbl}>
      {label}
      {children}
      {fieldErr[k] && <span role="alert" style={{ display: 'block', color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErr[k]}</span>}
    </label>
  )

  const errStyle = (k: keyof NewHookFields) => fieldErr[k] ? { ...inp, borderColor: '#b91c1c' } : inp

  return (
    <div style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 8, padding: 12, background: 'var(--color-bg-soft, #fafafa)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
        <Field k="name" label="Name">
          <input style={errStyle('name')} value={f.name} onChange={set('name')} aria-invalid={!!fieldErr.name}
            placeholder="e.g. Riverside Clinic EHR intake" />
        </Field>
        <Field k="url" label="Endpoint URL (https)">
          <input style={errStyle('url')} value={f.url} onChange={set('url')} aria-invalid={!!fieldErr.url}
            placeholder="https://partner.example.com/owlrx/intake" />
        </Field>
        <Field k="username" label="Username">
          <input style={inp} value={f.username} onChange={set('username')} autoComplete="off" />
        </Field>
        <Field k="password" label="Password">
          <input style={inp} type="password" value={f.password} onChange={set('password')} autoComplete="new-password" />
        </Field>
        <Field k="signingSecret" label="Signing secret (optional, HMAC-SHA256)">
          <input style={inp} type="password" value={f.signingSecret} onChange={set('signingSecret')} autoComplete="new-password" />
        </Field>
        {/* Timeout and rate/min are PLATFORM settings (owner, 2026-09-18) — how long we hold a
            connection open on the pharmacy's behalf, and how fast we push. Shown so the operator
            knows what they get, not offered as a choice. The server ignores them on this route too. */}
        <div style={{ alignSelf: 'end', fontSize: 12, color: 'var(--color-text-soft)', lineHeight: 1.6 }}>
          <div><strong>Timeout</strong> {f.timeoutSec}s · <strong>Rate</strong> {f.rateLimitPerMin}/min</div>
          <div style={{ fontSize: 11 }}>Set by OwlRx. Contact your account executive to change them.</div>
        </div>
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border, #eee)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Provider filter — {npiSel.size} NPI · {grpSel.size} group{grpSel.size === 1 ? '' : 's'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
          An envelope is sent to this hook only when its <strong>writing prescriber</strong> is one of these
          NPIs or a member of one of these groups. <strong>Leave it empty and the hook receives nothing</strong> —
          you can also set it later.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          <div>
            <div style={lbl}>Providers (by NPI)</div>
            <MultiSelectDropdown items={npiItems} selected={npiSel} onChange={setNpiSel}
              placeholder="Select prescribers…" emptyLabel="No mirrored prescribers with an NPI" />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input style={{ ...inp, flex: 1 }} value={extraNpi} onChange={e => setExtraNpi(e.target.value)}
                placeholder="Add an NPI not listed (10 digits)" inputMode="numeric"
                aria-label="Add a provider NPI not in the list" />
              <button type="button" style={btn} onClick={addExtraNpi}>Add</button>
            </div>
          </div>
          <div>
            <div style={lbl}>Provider groups</div>
            <MultiSelectDropdown items={groupItems} selected={grpSel} onChange={setGrpSel}
              placeholder="Select groups…" emptyLabel="No provider groups defined" />
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 10 }}>
        Created as <em>Pending</em>. Run a test send, then activate.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" style={btnPrimary} disabled={busy} onClick={submit}>{busy ? <Loader2 size={13} className="spin" /> : <Plus size={13} />} Create webhook</button>
        <button type="button" style={btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function HookEditor({ hook, providers, groups, onChanged, onError }: {
  hook: api.WebhookRow; providers: api.DataApiProvider[]; groups: { id: string; name: string }[]; onChanged: () => void; onError: (e: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState({ name: hook.name, url: hook.url, username: hook.auth_username ?? '', password: '', signingSecret: '', timeoutSec: String(hook.timeout_sec), rateLimitPerMin: String(hook.rate_limit_per_min) })
  const [npiSel, setNpiSel] = useState<Set<string>>(new Set(hook.npis))
  const [grpSel, setGrpSel] = useState<Set<string>>(new Set(hook.group_ids))
  const [extraNpi, setExtraNpi] = useState('')
  const [test, setTest] = useState<api.WebhookTestResult | null>(null)
  const [tab, setTab] = useState<'queue' | 'history'>('queue')
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })
  async function run(fn: () => Promise<unknown>) { setBusy(true); try { await fn(); onChanged() } catch (e) { onError(friendlyError(e)) } finally { setBusy(false) } }

  // Provider picker keyed by NPI (one entry per NPI; a prescriber without an NPI cannot be filtered on).
  const npiItems = Array.from(new Map(providers.filter(p => p.npi).map(p => [p.npi as string, { id: p.npi as string, label: `${p.name} · ${p.npi}` }])).values())
  hook.npis.forEach(n => { if (!npiItems.some(i => i.id === n)) npiItems.push({ id: n, label: `NPI ${n} (not a mirrored prescriber)` }) })

  const saveConfig = () => run(() => api.updateWebhook(hook.id, {
    name: f.name.trim(), url: f.url.trim(), username: f.username.trim(),
    password: f.password || undefined, signingSecret: f.signingSecret || undefined,
    timeoutSec: Number(f.timeoutSec) || undefined, rateLimitPerMin: Number(f.rateLimitPerMin) || undefined,
  }).then(() => setF({ ...f, password: '', signingSecret: '' })))
  const saveFilters = () => run(() => api.setWebhookFilters(hook.id, [...npiSel], [...grpSel]))
  const addExtraNpi = () => { const n = extraNpi.trim(); if (/^\d{10}$/.test(n)) { setNpiSel(new Set([...npiSel, n])); if (!npiItems.some(i => i.id === n)) npiItems.push({ id: n, label: `NPI ${n}` }); setExtraNpi('') } else onError('An NPI is 10 digits') }
  const doTest = async () => { setBusy(true); setTest(null); try { setTest(await api.testWebhook(hook.id)) } catch (e) { onError(friendlyError(e)) } finally { setBusy(false) } }
  const del = () => { if (confirm(`Delete webhook "${hook.name}"? Queued deliveries stop; history is kept.`)) run(() => api.deleteWebhook(hook.id)) }

  return (
    <div style={{ padding: '4px 14px 14px', borderTop: '1px solid var(--color-border, #eee)' }}>
      {hook.status === 'paused' && (
        <div style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', margin: '10px 0' }}>
          Paused after {hook.consecutive_failures} consecutive failures{hook.last_error ? ` — last error: ${hook.last_error}` : ''}. Matching envelopes are still being queued. Fix the endpoint, run a test, then <strong>Resume</strong>.
        </div>
      )}
      <Sub title="Status">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {hook.status !== 'active' && hook.status !== 'paused' && <button type="button" style={btn} disabled={busy} onClick={() => run(() => api.updateWebhook(hook.id, { status: 'active' }))}><Play size={13} /> Activate</button>}
          {hook.status === 'paused' && <button type="button" style={btn} disabled={busy} onClick={() => run(() => api.resumeWebhook(hook.id))}><Play size={13} /> Resume</button>}
          {hook.status === 'active' && <button type="button" style={btn} disabled={busy} onClick={() => run(() => api.updateWebhook(hook.id, { status: 'paused' }))}><Pause size={13} /> Pause</button>}
          <button type="button" style={btn} disabled={busy} onClick={doTest}><Send size={13} /> Test send</button>
          <button type="button" style={{ ...btn, color: '#dc2626', borderColor: '#f3c9c9' }} disabled={busy} onClick={del}><Trash2 size={13} /> Delete</button>
          <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
            {hook.last_success_at ? `last success ${formatUsDate(hook.last_success_at)}` : 'never delivered'}{hook.last_failure_at ? ` · last failure ${formatUsDate(hook.last_failure_at)}` : ''}
          </span>
        </div>
        {test && (
          <div style={{ marginTop: 8, fontSize: 12.5, padding: '7px 10px', borderRadius: 6, border: `1px solid ${test.ok ? '#86efac' : '#fecaca'}`, background: test.ok ? '#f0fdf4' : '#fef2f2' }}>
            Test send {test.ok ? 'succeeded' : 'failed'}: {test.status ?? 'no response'} in {test.ms} ms{test.error ? ` — ${test.error}` : ''}
            {test.response_snippet && <div style={{ fontFamily: 'monospace', fontSize: 11, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{test.response_snippet}</div>}
            <div style={{ color: 'var(--color-text-soft)', marginTop: 4 }}>A test carries a synthetic, PHI-free body with <code>X-OwlRx-Event: owlrx.test</code>.</div>
          </div>
        )}
      </Sub>

      <Sub title="Endpoint">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
          <label style={lbl}>Name <input style={inp} value={f.name} onChange={set('name')} /></label>
          <label style={lbl}>URL (https) <input style={inp} value={f.url} onChange={set('url')} /></label>
          <label style={lbl}>Username <input style={inp} value={f.username} onChange={set('username')} autoComplete="off" /></label>
          <label style={lbl}>Password {hook.password_set ? <span style={{ color: '#16a34a' }}>(set — leave blank to keep)</span> : <span style={{ color: '#d97706' }}>(not set)</span>}
            <input style={inp} type="password" value={f.password} onChange={set('password')} autoComplete="new-password" placeholder={hook.password_set ? '••••••••' : ''} /></label>
          <label style={lbl}>Signing secret {hook.signing_secret_set ? <span style={{ color: '#16a34a' }}>(set)</span> : <span style={{ color: 'var(--color-text-soft)' }}>(optional)</span>}
            <input style={inp} type="password" value={f.signingSecret} onChange={set('signingSecret')} autoComplete="new-password" /></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Platform-set; see the note on the create form. */}
            <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
              <div style={lbl}>Timeout</div>{f.timeoutSec}s <span style={{ fontSize: 11 }}>(set by OwlRx)</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
              <div style={lbl}>Rate / min</div>{f.rateLimitPerMin} <span style={{ fontSize: 11 }}>(set by OwlRx)</span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 8 }}><button type="button" style={btnPrimary} disabled={busy} onClick={saveConfig}>Save endpoint</button></div>
      </Sub>

      <Sub title={`Provider filter — ${npiSel.size} NPI · ${grpSel.size} group${grpSel.size === 1 ? '' : 's'}`}>
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 6 }}>
          An envelope is sent to this hook only when its <strong>writing prescriber</strong> is one of these NPIs or a member of one of these provider groups. Nothing else is ever sent.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={lbl}>Providers (by NPI)</div>
            <MultiSelectDropdown items={npiItems} selected={npiSel} onChange={setNpiSel} placeholder="Search providers…" emptyLabel="No prescribers with an NPI." />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input style={{ ...inp, flex: 1 }} value={extraNpi} onChange={e => setExtraNpi(e.target.value)} placeholder="Add an NPI not listed (10 digits)" inputMode="numeric" aria-label="Add a provider NPI to this webhook filter" />
              <button type="button" style={btn} onClick={addExtraNpi}>Add</button>
            </div>
          </div>
          <div>
            <div style={lbl}>Provider groups</div>
            {groups.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>No provider groups yet — create one under Groups (type: Providers).</div>
              : <MultiSelectDropdown items={groups.map(g => ({ id: g.id, label: g.name }))} selected={grpSel} onChange={setGrpSel} placeholder="Search groups…" emptyLabel="No groups." />}
            <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 6 }}>Group membership is read when each envelope arrives, so editing a group changes future deliveries only.</div>
          </div>
        </div>
        <div style={{ marginTop: 8 }}><button type="button" style={btnPrimary} disabled={busy} onClick={saveFilters}>Save filter</button></div>
      </Sub>

      <Sub title="Deliveries">
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <button type="button" style={{ ...btn, ...(tab === 'queue' ? tabActive : {}) }} onClick={() => setTab('queue')}><ListOrdered size={13} /> Queue ({hook.queued + hook.sending})</button>
          <button type="button" style={{ ...btn, ...(tab === 'history' ? tabActive : {}) }} onClick={() => setTab('history')}><History size={13} /> History</button>
        </div>
        <DeliveriesTable hookId={hook.id} scope={tab} onError={onError} />
      </Sub>
    </div>
  )
}

function DeliveriesTable({ hookId, scope, onError }: { hookId: string; scope: 'queue' | 'history'; onError: (e: string) => void }) {
  const [status, setStatus] = useState<string>('')
  const [q, setQ] = useState('')
  const [tick, setTick] = useState(0)
  const [detail, setDetail] = useState<{ id: string; text: string } | null>(null)
  const effective = status || (scope === 'queue' ? 'pending' : 'history')
  // D-UI-2: header funnels. Server-paged, so they go to the endpoint (`filter=`) and AND with the
  // toolbar above. The status list and the date column follow the tab, as the table does.
  const funnelCols = useMemo<ServerFunnelColumn[]>(() => [
    { key: 'received_at', label: 'Received', kind: 'date' },
    { key: 'rx_number', label: 'Rx', kind: 'text' },
    { key: 'matched_npi', label: 'Provider NPI', kind: 'text' },
    { key: 'event', label: 'Event', kind: 'text' },
    { key: 'status', label: 'Status', kind: 'list', options: scope === 'queue' ? ['queued', 'sending'] : ['sent', 'failed', 'dead'] },
    { key: 'attempts', label: 'Attempts', kind: 'number' },
    scope === 'queue'
      ? { key: 'next_attempt_at', label: 'Next try', kind: 'date' }
      : { key: 'sent_or_last_attempt_at', label: 'Sent / last try', kind: 'date' },
    { key: 'response', label: 'Response', kind: 'text' },
  ], [scope])
  const fx = useServerFunnels(funnelCols)
  const dateKey = scope === 'queue' ? 'next_attempt_at' : 'sent_or_last_attempt_at'
  const paged = useServerPaged<api.WebhookDelivery>(
    ({ page, pageSize }) => api.listWebhookDeliveries(hookId, { status: effective, q: q || undefined, page, page_size: pageSize, filter: fx.filter }),
    DEFAULT_PAGE_SIZE, [hookId, effective, q, tick, fx.sig])
  // Queue and History are two tables: a funnel set on one (a status the other never holds) would
  // empty the other, so they reset with the tab, as the status buttons already do.
  useEffect(() => { setStatus(''); fx.clear() }, [scope])  // eslint-disable-line react-hooks/exhaustive-deps
  const replay = async (d: api.WebhookDelivery) => { try { await api.replayWebhookDelivery(hookId, d.id); setTick(t => t + 1) } catch (e) { onError(friendlyError(e)) } }
  const showDetail = async (d: api.WebhookDelivery) => {
    try { const r = await api.getWebhookDeliveryDetail(hookId, d.id); setDetail({ id: d.id, text: [r.last_error, r.response_snippet].filter(Boolean).join('\n') || '(no response body recorded)' }) }
    catch (e) { onError(friendlyError(e)) }
  }
  const statuses = scope === 'queue' ? ['queued', 'sending'] : ['sent', 'failed', 'dead']
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        {statuses.map(s => <button key={s} type="button" style={{ ...btn, ...(status === s ? tabActive : {}) }} onClick={() => setStatus(status === s ? '' : s)}>{s}</button>)}
        <input style={{ ...inp, width: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Rx number or message id" aria-label="Search the delivery queue by Rx number or message id" />
        <button type="button" style={btn} onClick={() => setTick(t => t + 1)} title="Refresh"><RefreshCw size={13} /></button>
        {paged.loading && <Loader2 size={13} className="spin" />}
      </div>
      {fx.chips}
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
        <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: 'var(--color-text-mute, #6b7280)', paddingTop: 6 }}>Envelopes waiting to be delivered to this webhook.</caption>
          <thead><tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border, #eee)' }}>
            <th style={th}>Received{fx.funnel('received_at')}</th><th style={th}>Rx{fx.funnel('rx_number')}</th><th style={th}>Provider NPI{fx.funnel('matched_npi')}</th><th style={th}>Event{fx.funnel('event')}</th><th style={th}>Status{fx.funnel('status')}</th><th style={th}>Attempts{fx.funnel('attempts')}</th><th style={th}>{scope === 'queue' ? 'Next try' : 'Sent / last try'}{fx.funnel(dateKey)}</th><th style={th}>Response{fx.funnel('response')}</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {paged.rows.length === 0 && <tr><td colSpan={9} style={{ ...td, color: 'var(--color-text-soft)' }}>{paged.loading ? 'Loading…' : fx.active ? 'No deliveries match these filters.' : scope === 'queue' ? 'Nothing waiting to be sent.' : 'No deliveries yet.'}</td></tr>}
            {paged.rows.map(d => (
              <tr key={d.id} style={{ borderBottom: '1px solid var(--color-border, #f4f4f4)' }}>
                <td style={td} title={d.message_id ?? ''}>{formatUsDate(d.received_at)}</td>
                <td style={td}>{d.rx_number ?? '—'}</td>
                <td style={td}>{d.matched_npi ?? '—'}{d.matched_by === 'provider_group' ? <span style={{ color: 'var(--color-text-soft)' }}> (group)</span> : d.matched_by === 'replay' ? <span style={{ color: 'var(--color-text-soft)' }}> (replay)</span> : null}</td>
                <td style={td}>{d.initiating_event_id ?? '—'}</td>
                <td style={td}><DeliveryStatus status={d.status} /></td>
                <td style={td}>{d.attempts}</td>
                <td style={td}>{scope === 'queue' ? (d.next_attempt_at ? formatUsDate(d.next_attempt_at) : '—') : (d.sent_at ? formatUsDate(d.sent_at) : d.last_attempt_at ? formatUsDate(d.last_attempt_at) : '—')}</td>
                <td style={td} title={d.last_error ?? ''}>{d.response_status ?? (d.last_error ? d.last_error.slice(0, 40) : '—')}{d.response_ms != null ? ` · ${d.response_ms} ms` : ''}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button type="button" style={linkBtn} onClick={() => showDetail(d)}>details</button>
                  {(d.status === 'sent' || d.status === 'failed' || d.status === 'dead') && <button type="button" style={{ ...linkBtn, marginLeft: 8 }} onClick={() => replay(d)}><RotateCcw size={11} /> replay</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && (
        <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', padding: 8, background: 'var(--color-bg-soft, #fafafa)', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>delivery {detail.id}</span><button type="button" style={linkBtn} onClick={() => setDetail(null)}>close</button></div>
          {detail.text}
        </div>
      )}
      <div style={{ marginTop: 6 }}><Pager {...paged.pagerProps} label="deliveries" /></div>
    </div>
  )
}

// ── Pharmacy-wide delivery log — EVERY envelope sent / waiting / failed to ANY partner hook ─────────
// Patrick 2026-09-09: "a queue showing all the envelopes we sent for compliance and tracking and
// troubleshooting." One server-paged log across hooks, with status / window / hook / NPI / search
// filters, summary tiles that agree with the table, details + replay per row, and a CSV export
// (audited) for the compliance file.
export function WebhookDeliveriesPanel({ onError, partners }: {
  onError: (e: string) => void
  /** Partner names, so the "Partner · hook" funnel can read as the cell does. Optional. */
  partners?: { id: string; name: string }[]
}) {
  const [hooks, setHooks] = useState<api.WebhookRow[]>([])
  const [status, setStatus] = useState<string>('')
  const [sinceHours, setSinceHours] = useState<number | undefined>(24 * 7)
  const [hookId, setHookId] = useState<string>('')
  const [npi, setNpi] = useState('')
  const [q, setQ] = useState('')
  const [tick, setTick] = useState(0)
  const [summary, setSummary] = useState<{ queued: number; sending: number; sent: number; failed: number; dead: number } | null>(null)
  const [detail, setDetail] = useState<{ id: string; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { api.listWebhooks().then(l => setHooks(l.hooks)).catch(() => { /* optional */ }) }, [tick])
  // D-UI-2: header funnels. Server-paged, so they go to the endpoint (`filter=`) and AND with the
  // toolbar above. "Partner · hook" is named on the control plane, not in the delivery query, so
  // its funnel ticks the hooks this page already lists and the endpoint matches their ids.
  const funnelCols = useMemo<ServerFunnelColumn[]>(() => [
    { key: 'received_at', label: 'Received', kind: 'date' },
    { key: 'sent', label: 'Sent', kind: 'date' },
    { key: 'hook', label: 'Partner · hook', kind: 'list', options: hooks.map(h => {
      const partner = partners?.find(c => c.id === h.client_id)?.name
      return { value: h.id, label: partner ? `${partner} · ${h.name}` : h.name }
    }) },
    { key: 'rx_number', label: 'Rx', kind: 'text' },
    { key: 'matched_npi', label: 'Provider NPI', kind: 'text' },
    { key: 'event', label: 'Event', kind: 'text' },
    { key: 'status', label: 'Status', kind: 'list', options: ['queued', 'sending', 'sent', 'failed', 'dead'] },
    { key: 'attempts', label: 'Attempts', kind: 'number' },
    { key: 'response', label: 'Response', kind: 'text' },
    { key: 'disclosure', label: 'Disclosure', kind: 'text' },
  ], [hooks, partners])
  const fx = useServerFunnels(funnelCols)
  // The CSV export takes the same params, so it exports what the table is showing.
  const params = { status: status || undefined, hook_id: hookId || undefined, npi: npi || undefined, q: q || undefined, since_hours: sinceHours, filter: fx.filter }
  const paged = useServerPaged<api.WebhookDeliveryAll>(
    async ({ page, pageSize }) => { const r = await api.listAllWebhookDeliveries({ ...params, page, page_size: pageSize }); setSummary(r.summary); return r },
    DEFAULT_PAGE_SIZE, [status, hookId, npi, q, sinceHours, tick, fx.sig])
  const replay = async (d: api.WebhookDeliveryAll) => { try { await api.replayWebhookDelivery(d.endpoint_id, d.id); setTick(t => t + 1) } catch (e) { onError(friendlyError(e)) } }
  const showDetail = async (d: api.WebhookDeliveryAll) => {
    try { const r = await api.getWebhookDeliveryDetail(d.endpoint_id, d.id); setDetail({ id: d.id, text: [`status ${r.status} · attempts ${r.attempts} · response ${r.response_status ?? '—'} in ${r.response_ms ?? '—'} ms`, r.last_error, r.response_snippet].filter(Boolean).join('\n') }) }
    catch (e) { onError(friendlyError(e)) }
  }
  const exportCsv = async () => {
    setBusy(true)
    try {
      const blob = await api.exportWebhookDeliveriesCsv(params)
      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `webhook-deliveries-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (e) { onError(friendlyError(e)) } finally { setBusy(false) }
  }
  const tile = (label: string, n: number, color: string, key: string) => (
    <button type="button" key={key} onClick={() => setStatus(status === key ? '' : key)}
      style={{ ...btn, flexDirection: 'column', alignItems: 'flex-start', minWidth: 92, padding: '6px 10px', ...(status === key ? tabActive : {}) }}>
      <span style={{ fontSize: 18, fontWeight: 700, color }}>{n}</span><span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{label}</span>
    </button>
  )
  return (
    <div style={{ border: '1px solid var(--color-border, #eee)', borderRadius: 8, padding: 14, margin: '6px 0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <ListOrdered size={15} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>Webhook deliveries — every envelope sent to any partner</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button type="button" style={btn} onClick={() => setTick(t => t + 1)} title="Refresh"><RefreshCw size={13} /></button>
          <button type="button" style={btn} disabled={busy} onClick={exportCsv} title="CSV of the current filter (audited)"><Download size={13} /> Export CSV</button>
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 10 }}>
        One row per envelope × hook. Sent rows carry the disclosure record id (HIPAA §164.528 accounting); queued rows are waiting for the next send window; failed / dead rows can be replayed.
      </div>
      {summary && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {tile('queued', summary.queued, '#d97706', 'queued')}{tile('sending', summary.sending, '#2563eb', 'sending')}{tile('sent', summary.sent, '#16a34a', 'sent')}{tile('failed', summary.failed, '#dc2626', 'failed')}{tile('dead', summary.dead, '#6b7280', 'dead')}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>Window:</span>
        {([[24, '24 h'], [24 * 7, '7 days'], [24 * 30, '30 days'], [undefined, 'All']] as [number | undefined, string][]).map(([h, l]) => (
          <button key={l} type="button" style={{ ...btn, ...(sinceHours === h ? tabActive : {}) }} onClick={() => setSinceHours(h)}>{l}</button>
        ))}
        <span style={{ fontSize: 11, color: 'var(--color-text-soft)', marginLeft: 8 }}>Hook:</span>
        <button type="button" style={{ ...btn, ...(hookId === '' ? tabActive : {}) }} onClick={() => setHookId('')}>All</button>
        {hooks.map(h => <button key={h.id} type="button" style={{ ...btn, ...(hookId === h.id ? tabActive : {}) }} onClick={() => setHookId(hookId === h.id ? '' : h.id)} title={h.url}>{h.name}</button>)}
        <input style={{ ...inp, width: 130 }} value={npi} onChange={e => setNpi(e.target.value)} placeholder="Provider NPI" inputMode="numeric" aria-label="Filter deliveries by provider NPI" />
        <input style={{ ...inp, width: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Rx number, message id, delivery id" aria-label="Search deliveries by Rx number, message id or delivery id" />
        {paged.loading && <Loader2 size={13} className="spin" />}
      </div>
      {fx.chips}
      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
        <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: 'var(--color-text-mute, #6b7280)', paddingTop: 6 }}>Delivery history across all of this pharmacy’s webhooks.</caption>
          <thead><tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border, #eee)' }}>
            <th style={th}>Received{fx.funnel('received_at')}</th><th style={th}>Sent{fx.funnel('sent')}</th><th style={th}>Partner · hook{fx.funnel('hook')}</th><th style={th}>Rx{fx.funnel('rx_number')}</th><th style={th}>Provider NPI{fx.funnel('matched_npi')}</th><th style={th}>Event{fx.funnel('event')}</th><th style={th}>Status{fx.funnel('status')}</th><th style={th}>Attempts{fx.funnel('attempts')}</th><th style={th}>Response{fx.funnel('response')}</th><th style={th}>Disclosure{fx.funnel('disclosure')}</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {paged.rows.length === 0 && <tr><td colSpan={11} style={{ ...td, color: 'var(--color-text-soft)' }}>{paged.loading ? 'Loading…' : 'No deliveries match this filter.'}</td></tr>}
            {paged.rows.map(d => (
              <tr key={d.id} style={{ borderBottom: '1px solid var(--color-border, #f4f4f4)' }}>
                <td style={td} title={`message ${d.message_id ?? '—'} · sha256 ${d.envelope_sha256}`}>{formatUsDate(d.received_at)}</td>
                <td style={td}>{d.sent_at ? formatUsDate(d.sent_at) : d.status === 'queued' && d.next_attempt_at ? <span style={{ color: 'var(--color-text-soft)' }}>next {formatUsDate(d.next_attempt_at)}</span> : '—'}</td>
                <td style={td}>{d.client_name ? `${d.client_name} · ` : ''}{d.hook_name}</td>
                <td style={td}>{d.rx_number ?? '—'}</td>
                <td style={td}>{d.matched_npi ?? '—'}{d.matched_by === 'provider_group' ? <span style={{ color: 'var(--color-text-soft)' }}> (group)</span> : d.matched_by === 'replay' ? <span style={{ color: 'var(--color-text-soft)' }}> (replay)</span> : null}</td>
                <td style={td}>{d.initiating_event_id ?? '—'}</td>
                <td style={td}><DeliveryStatus status={d.status} /></td>
                <td style={td}>{d.attempts}</td>
                <td style={td} title={d.last_error ?? ''}>{d.response_status ?? (d.last_error ? d.last_error.slice(0, 40) : '—')}{d.response_ms != null ? ` · ${d.response_ms} ms` : ''}</td>
                <td style={td} title={d.disclosure_id ?? ''}>{d.disclosure_id ? d.disclosure_id.slice(0, 8) : '—'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button type="button" style={linkBtn} onClick={() => showDetail(d)}>details</button>
                  {(d.status === 'sent' || d.status === 'failed' || d.status === 'dead') && <button type="button" style={{ ...linkBtn, marginLeft: 8 }} onClick={() => replay(d)}><RotateCcw size={11} /> replay</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && (
        <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', padding: 8, background: 'var(--color-bg-soft, #fafafa)', border: '1px solid var(--color-border, #eee)', borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>delivery {detail.id}</span><button type="button" style={linkBtn} onClick={() => setDetail(null)}>close</button></div>
          {detail.text}
        </div>
      )}
      <div style={{ marginTop: 6 }}><Pager {...paged.pagerProps} label="deliveries" /></div>
    </div>
  )
}

function DeliveryStatus({ status }: { status: string }) {
  const map: Record<string, string> = { queued: '#d97706', sending: '#2563eb', sent: '#16a34a', failed: '#dc2626', dead: '#6b7280' }
  return <span style={{ fontSize: 11, fontWeight: 600, color: map[status] ?? '#6b7280', border: `1px solid ${map[status] ?? '#6b7280'}`, borderRadius: 10, padding: '0 7px' }}>{status}</span>
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-text-soft)', marginBottom: 5 }}>{title}</div>
      {children}
    </div>
  )
}

const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--color-text-soft)' }
const inp: React.CSSProperties = { fontSize: 13, padding: '6px 8px', border: '1px solid var(--color-border, #ddd)', borderRadius: 5, background: 'var(--color-bg, #fff)' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 10px', border: '1px solid var(--color-border, #ddd)', borderRadius: 5, background: 'var(--color-bg, #fff)', cursor: 'pointer' }
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--color-accent, #2563eb)', color: '#fff', border: '1px solid var(--color-accent, #2563eb)' }
const tabActive: React.CSSProperties = { background: 'var(--color-accent-subtle, #eff4ff)', borderColor: 'var(--color-accent, #2563eb)', color: 'var(--color-accent, #2563eb)', fontWeight: 600 }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--color-accent, #2563eb)', display: 'inline-flex', alignItems: 'center', gap: 3 }
const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '5px 8px', whiteSpace: 'nowrap' }
